import { EmptyState, ErrorState, LoadingState, RADIUS } from "@elyxeer/ui";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import {
  deleteAdminEventRegistration,
  exportAdminEventRegistrations,
  fetchAdminEventRegistrations,
} from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { EventRegistrationAdmin } from "~/types/events-admin";

const PAGE_SIZE = 50;

/** Hand the browser a file to save. */
function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Who booked a place: members from the app and the public from the registration page,
 * newest first, with the CSV the marketing team takes away.
 *
 * Pending rows are shown rather than hidden so the editor can see how many addresses
 * never confirmed — a form that only ever showed confirmed places would make the
 * double opt-in look like a leak.
 */
export function EventRegistrations({
  eventId,
  slug,
}: {
  eventId: string;
  slug: string;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<EventRegistrationAdmin | null>(null);

  const query = useInfiniteQuery({
    queryKey: queryKeys.eventRegistrations(eventId),
    queryFn: ({ pageParam, signal }) =>
      fetchAdminEventRegistrations(eventId, {
        limit: PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const exportCsv = useMutation({
    mutationFn: () => exportAdminEventRegistrations(eventId),
    onSuccess: (blob) => download(blob, `${slug}-registrations.csv`),
  });

  const remove = useMutation({
    mutationFn: (registrationId: string) =>
      deleteAdminEventRegistration(eventId, registrationId),
    onSuccess: async () => {
      setRemoving(null);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.eventRegistrations(eventId),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.event(eventId) }),
      ]);
    },
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const dateOptions = {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  return (
    <Paper
      component="section"
      variant="outlined"
      data-testid="event.registrations"
      sx={{
        padding: 3,
        borderRadius: `${RADIUS.lg}px`,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="H3" sx={{ color: "text.primary" }}>
            {t("eventEditor.registrations.title")}
          </Typography>
          <Typography variant="T3" sx={{ color: "text.muted", marginTop: 0.5 }}>
            {t("eventEditor.registrations.hint")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadOutlinedIcon fontSize="small" />}
          disabled={exportCsv.isPending || total === 0}
          onClick={() => exportCsv.mutate()}
          data-testid="event.registrations-export"
        >
          {t("eventEditor.registrations.export")}
        </Button>
      </Box>

      {query.isPending ? <LoadingState /> : null}
      {query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && rows.length === 0 ? (
        <EmptyState message={t("eventEditor.registrations.empty")} />
      ) : null}

      {rows.length > 0 ? (
        <>
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {t("eventEditor.registrations.count", {
              loaded: rows.length,
              total,
            })}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" data-testid="event.registrations-table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("eventEditor.registrations.columns.email")}
                  </TableCell>
                  <TableCell>
                    {t("eventEditor.registrations.columns.name")}
                  </TableCell>
                  <TableCell>
                    {t("eventEditor.registrations.columns.status")}
                  </TableCell>
                  <TableCell>
                    {t("eventEditor.registrations.columns.source")}
                  </TableCell>
                  <TableCell>
                    {t("eventEditor.registrations.columns.locale")}
                  </TableCell>
                  <TableCell>
                    {t("eventEditor.registrations.columns.consent")}
                  </TableCell>
                  <TableCell>
                    {t("eventEditor.registrations.columns.registered")}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`event.registration-${row.email}`}
                  >
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.display_name ?? "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={
                          row.status === "confirmed" ? "success" : "default"
                        }
                        label={t(
                          `eventEditor.registrations.status.${row.status}`,
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      {t(`eventEditor.registrations.source.${row.source}`)}
                    </TableCell>
                    <TableCell>{row.locale}</TableCell>
                    <TableCell>
                      {row.marketing_consent ? t("common.yes") : t("common.no")}
                    </TableCell>
                    <TableCell>
                      <time dateTime={row.created_at}>
                        {formatDate(row.created_at, dateOptions)}
                      </time>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        variant="text"
                        onClick={() => setRemoving(row)}
                        data-testid={`event.registration-remove-${row.email}`}
                      >
                        {t("eventEditor.registrations.remove")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {query.hasNextPage ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => void query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
              sx={{ alignSelf: "center" }}
            >
              {t("eventEditor.registrations.loadMore")}
            </Button>
          ) : null}
        </>
      ) : null}

      <Dialog open={removing !== null} onClose={() => setRemoving(null)}>
        <DialogTitle>
          {t("eventEditor.registrations.removeConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("eventEditor.registrations.removeConfirmBody", {
              email: removing?.email ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)} disabled={remove.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={() => removing && remove.mutate(removing.id)}
            data-testid="event.registration-remove-confirm"
          >
            {t("eventEditor.registrations.removeConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
